import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDate, isExpired } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useSubSuperAdmins, useSwitchUser } from '../api';
import { UserRowActions } from '../components/UserRowActions';
import type { SubSuperAdminRow, UserRow } from '../types';

/** Adapt the sub-super-admin list row to the shape the User Console expects. */
function toUserRow(r: SubSuperAdminRow): UserRow {
  return {
    userId: r.userId,
    username: r.username,
    name: r.name ?? r.username,
    company: r.company,
    emailid: r.emailid,
    number: r.number,
    userType: 'sub_sadmin',
    credits: 0,
    expiryDate: r.expiryDate,
    reqDate: r.reqDate,
    accountType: r.accountType ?? 0,
    deleted: r.deleted,
    parentUser: r.parentUser,
    aclId: r.aclId,
  };
}

export function SubSuperAdminListPage() {
  const query = useSubSuperAdmins();
  const rows = query.data ?? [];
  const navigate = useNavigate();
  const switchTo = useAuthStore((s) => s.switchTo);
  const switchMutation = useSwitchUser();

  const handleSwitch = useCallback(
    (row: SubSuperAdminRow) => {
      switchMutation.mutate(row.userId, {
        onSuccess: (res) => {
          if (!res.token) {
            toast.error((res as { message?: string }).message || 'Cannot switch — user account may be expired or deleted');
            return;
          }
          const incomingRole = Array.isArray(res.role) ? res.role[0] : res.role;
          if (!incomingRole) {
            toast.error('Switch response missing role');
            return;
          }
          switchTo({
            user: { userId: String(res.userid), role: incomingRole },
            token: res.token,
          });
          toast.success(`Switched to ${row.username}`);
          navigate(incomingRole === USER_ROLES.SUB_USER ? '/sub-user-report' : '/', { replace: true });
        },
        onError: () => toast.error('Cannot switch — user account may be expired or deleted'),
      });
    },
    [navigate, switchMutation, switchTo],
  );

  const columns = useMemo<ColumnDef<SubSuperAdminRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <Link
            to={`/sub-super-admin/update-user/${row.original.userId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.username}
          </Link>
        ),
      },
      { accessorKey: 'company', header: 'Company' },
      {
        accessorKey: 'parentUser',
        header: 'Parent',
        cell: ({ row }) => row.original.parentUser ?? '—',
      },
      {
        accessorKey: 'expiryDate',
        header: 'Expires',
        size: 130,
        cell: ({ row }) => {
          const v = row.original.expiryDate;
          if (!v) return '—';
          const date = new Date(v.replace(' ', 'T'));
          return Number.isFinite(date.getTime()) ? formatDate(date) : v;
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => {
          if (isExpired(row.original.expiryDate)) {
            return <Badge variant="warning">Expired</Badge>;
          }
          return row.original.deleted === 0 ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="destructive">Inactive</Badge>
          );
        },
      },
      {
        id: 'switchUser',
        header: 'Switch',
        size: 76,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={`Switch to ${row.original.username}`}
            onClick={() => handleSwitch(row.original)}
            disabled={switchMutation.isPending}
          >
            <LogIn className="h-4 w-4" />
          </Button>
        ),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <UserRowActions
            user={toUserRow(row.original)}
            editPathPrefix="/sub-super-admin/update-user"
            hideTtsCredits
          />
        ),
      },
    ],
    [switchMutation.isPending, handleSwitch],
  );

  return (
    <FillPage>
      <PageHeader
        title="Admins"
        description="Permission-scoped admins managed under your super-admin account."
        actions={
          <Button asChild>
            <Link to="/sub-super-admin/create-user">
              <Plus className="h-4 w-4" />
              Add sub super admin
            </Link>
          </Button>
        }
      />
      <DataTable<SubSuperAdminRow, unknown>
        tableId="users:sub-super-admin:v7"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search sub super admins…"
        emptyTitle="No sub super admins"
        emptyDescription="Create a sub super admin to delegate scoped admin access."
        getRowId={(row) => String(row.userId)}
        exportFilename="sub-super-admins.csv"
        initialColumnPinning={{ right: ['switchUser', 'actions'] }}
        pageSize={10}
      />
    </FillPage>
  );
}